import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * DashboardLiveSessionStatus
 *
 * Props:
 * - liveSession: live session object or null
 * - sessionLoading: boolean
 * - completingSession: boolean
 * - handleCancelSessionRequest: () => Promise<void>
 * - handleCompleteSession: () => Promise<void>
 */
const DashboardLiveSessionStatus = ({
  liveSession,
  sessionLoading,
  completingSession,
  handleCancelSessionRequest,
  handleCompleteSession
}) => {
  const navigate = useNavigate();

  if (sessionLoading || !liveSession || liveSession.status === 'completed') {
    return null;
  }

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-emerald-50 to-blue-50 dark:from-emerald-900/20 dark:to-blue-900/20">
        <div className="flex items-center">
          <svg
            className="w-6 h-6 text-emerald-600 dark:text-emerald-400 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
          <h3 className="text-lg font-semibold text-gray-900 dark:text:white">
            Live Assessment Session
          </h3>
        </div>
      </div>

      <div className="p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Consultant
                </label>
                <p className="text-base font-semibold text-gray-900 dark:text-white">
                  {liveSession.consultant?.name || 'Loading...'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Assessment Type
                </label>
                <p className="text-base font-semibold text-gray-900 dark:text-white">
                  {liveSession.assessment_type}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Status
                </label>
                <span
                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    liveSession.status === 'active'
                      ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                      : liveSession.status === 'pending'
                      ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                      : liveSession.status === 'completed'
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                      : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                  }`}
                >
                  {liveSession.status.charAt(0).toUpperCase() +
                    liveSession.status.slice(1)}
                </span>
              </div>
            </div>

            {liveSession.started_at && (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Started: {new Date(liveSession.started_at).toLocaleString()}
              </p>
            )}
          </div>

          <div className="flex-shrink-0">
            {liveSession.status === 'pending' && (
              <div className="flex flex-col gap-3">
                <div className="flex items-center text-yellow-700 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-900/30 px-4 py-3 rounded-lg border border-yellow-200 dark:border-yellow-800">
                  <svg
                    className="animate-spin h-5 w-5 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span className="text-sm font-medium">
                    Waiting for consultant to start
                  </span>
                </div>
                <button
                  onClick={handleCancelSessionRequest}
                  className="bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center text-sm"
                >
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  Cancel Request
                </button>
              </div>
            )}

            {liveSession.status === 'active' && (
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => navigate(`/live-session/${liveSession.id}`)}
                  className="bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center"
                >
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {liveSession.started_at ? 'Resume Session' : 'Join Live Session'}
                </button>
                {/* Only show Complete Assessment button if user has joined and answered all questions */}
                {liveSession.started_at ? (
                  (() => {
                    const totalQuestions = liveSession.questions_count || 0;
                    const answeredCount = liveSession.answered_count || 0;
                    const allQuestionsAnswered = totalQuestions > 0 && answeredCount >= totalQuestions;
                    
                    return allQuestionsAnswered ? (
                      <button
                        onClick={handleCompleteSession}
                        disabled={completingSession}
                        className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {completingSession ? (
                          <>
                            <svg
                              className="animate-spin h-5 w-5 mr-2"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              />
                            </svg>
                            Completing...
                          </>
                        ) : (
                          <>
                            <svg
                              className="w-5 h-5 mr-2"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            Complete Assessment
                          </>
                        )}
                      </button>
                    ) : (
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 px-4 py-3 rounded-lg">
                        <p className="text-sm text-yellow-800 dark:text-yellow-200 font-medium mb-1">
                          Complete all questions to finish the assessment
                        </p>
                        <p className="text-xs text-yellow-700 dark:text-yellow-300">
                          Progress: {answeredCount} of {totalQuestions} questions answered
                        </p>
                      </div>
                    );
                  })()
                ) : (
                  <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-4 py-3 rounded-lg text-center">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Please join the session first to complete the assessment
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardLiveSessionStatus;


